import { CSSProperties, ComponentType, LegacyRef, ReactNode, Ref } from 'react';
import { AddListener } from './modalDropdownTypes';
import { getPositionAndMaxHeightVertically } from '../dropdownHelpers';
import { ModalBackdrop } from './ModalBackdrop';
import { ModalBox, ModalBoxProps } from './ModalBox';
import { ModalList } from './ModalList';
import { OptionalPortal } from './OptionalPortal';
import { DropdownItem } from './types';
import { DropdownSize } from '@ardoq/dropdown-menu-ui';
import { WithDropdown } from './WithDropdown';

export type ComponentProps<T = any> = {
  ref: Ref<T> | LegacyRef<T>;
  onMenuOpen: VoidFunction;
  isMenuOpened: boolean;
};

export type ComponentWithDropdownComponentProps<T = unknown> = Omit<
  T,
  'isMenuOpened' | 'onMenuOpen'
>;
type ComponentWithDropdownProps<T = unknown> = {
  options?: Partial<DropdownItem>[];
  /**
   * Render arbitrary dropdown content instead of the default `ModalList` of
   * `options`. Receives `closeDropdown` so the content can dismiss the dropdown
   * itself (e.g. on selection or Escape). Takes precedence over `options`.
   */
  renderContent?: (args: { closeDropdown: VoidFunction }) => ReactNode;
  isKeepOpen?: boolean;
  /**
   * Forwarded to `ModalBackdrop`. Set when the dropdown content handles its own
   * keyboard interaction (typing in inputs, custom focus navigation) and the
   * backdrop's global key handling would interfere.
   */
  ignoreKeyboardEvents?: boolean;
  alignMenuTo?: 'left' | 'right';
  dropdownStyle?: CSSProperties;
  iconStyle?: CSSProperties;
  dropdownSize?: DropdownSize;
  Component: ComponentType<ComponentProps & T>;
  /**
   * By default ComponentWithDropdown has its ModalBackdrop and ModalBox wrapped
   * in a Portal. This prevents surrounding styles from affecting the style of
   * the dropdown. However, when used inside a dialog, any click inside or
   * outside the dropdown is interpreted as an outside click by the dialog,
   * causing the dialog to close. In order for ComponentWithDropdown to work as
   * intended inside a dialog, use `disablePortal: true`.
   */
  disablePortal?: boolean;
  componentProps?: ComponentWithDropdownComponentProps<T>;
  headerContent?: ModalBoxProps['headerContent'];
  footerContent?: ModalBoxProps['footerContent'];
  autoFocusActiveOption?: boolean;
  onMenuOpen?: VoidFunction;
  onMenuClose?: VoidFunction;
};

export const ComponentWithDropdown = <T,>({
  options,
  renderContent,
  isKeepOpen = false,
  ignoreKeyboardEvents,
  alignMenuTo = 'left',
  dropdownStyle,
  iconStyle,
  disablePortal,
  dropdownSize,
  Component,
  componentProps,
  headerContent,
  footerContent,
  autoFocusActiveOption,
  onMenuClose,
  onMenuOpen,
}: ComponentWithDropdownProps<T>) => {
  const forceAlignMenuToRight = alignMenuTo !== 'left';
  return (
    <WithDropdown>
      {(openModal, closeModal, isOpen, getHostBox, hostRef) => {
        const closeDropdown = () => {
          closeModal();
          if (onMenuClose) onMenuClose();
        };
        return (
          <>
            <Component
              ref={hostRef}
              onMenuOpen={() => {
                openModal();
                if (onMenuOpen) onMenuOpen();
              }}
              isMenuOpened={isOpen}
              {...(componentProps as T)}
            />
            {isOpen && (
              <OptionalPortal disablePortal={disablePortal}>
                <ModalBackdrop
                  closeModal={closeDropdown}
                  isKeepOpen={isKeepOpen}
                  ignoreKeyboardEvents={ignoreKeyboardEvents}
                >
                  {(
                    addKeyboardListener: AddListener,
                    removeKeyboardListener: AddListener
                  ) => (
                    <ModalBox
                      dropdownSize={dropdownSize}
                      getHostBox={getHostBox}
                      headerContent={headerContent}
                      footerContent={footerContent}
                      getPositionAndMaxHeight={(refBox, modalBox) =>
                        getPositionAndMaxHeightVertically({
                          refBox,
                          modalBox,
                          isScrollable: true,
                          forceAlignToRight: forceAlignMenuToRight,
                        })
                      }
                    >
                      {renderContent ? (
                        renderContent({ closeDropdown })
                      ) : (
                        <ModalList
                          options={options ?? []}
                          addKeyboardListener={addKeyboardListener}
                          removeKeyboardListener={removeKeyboardListener}
                          iconStyle={iconStyle}
                          dropdownStyle={dropdownStyle}
                          autoFocusActiveOption={autoFocusActiveOption}
                        />
                      )}
                    </ModalBox>
                  )}
                </ModalBackdrop>
              </OptionalPortal>
            )}
          </>
        );
      }}
    </WithDropdown>
  );
};
